import type { MouseEvent } from 'react';
import React, { useCallback } from 'react';
import { AccordionTrigger, Content, Item } from '@radix-ui/react-accordion';
import { keyframes, styled } from '@components-shared/src/stitches';
import { ChartCardAccordionDetails } from './AccordionDetails';
import { Arrow, ChartCardActions, HoverActions } from './Actions';
import { ChartCardButton } from './Button';
import { ChartCardCollectors } from './Collectors';
import { ChartCardContext } from './Context';
import {
  CHART_CARD_COVER_IMAGE_SIZES,
  ChartCardCover,
  CoverImageContainer,
  PlayButtonContainer,
} from './Cover';
import { ChartCardInfo } from './Info';
import { ChartCardRank } from './Rank';
import { AvatarsSkeleton, ChartCardSkeleton } from './Skeleton';
import { ChartCardStats } from './Stats';

const ANIMATION_TIMING = '400ms ease';
export const CHART_CARD_MARGIN = 16;

export const CHART_CARD_SIZE = {
  minimalMobile: 92,
  mobile: 112,
  tablet: 312,
  desktop: 356,
} as const;

export function ChartCard(
  props: {
    children: React.ReactNode;
    id: string;
    accordionDetails?: JSX.Element;
    liked: boolean;
  } & React.ContextType<typeof ChartCardContext>,
) {
  const { id, minimal, expandedId, setExpandedId, liked } = props;

  const handleOnSummaryClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      return setExpandedId?.(expandedId === id ? null : id);
    },
    [expandedId, id, setExpandedId],
  );

  return (
    <ChartCardContext.Provider value={{ ...props }}>
      {minimal && (
        <ChartCardContainer liked={liked} minimal>
          {props.children}
        </ChartCardContainer>
      )}

      {!minimal && (
        <>
          <div className="hidden md:block">
            <StyledItem value={props.id}>
              <StyledTrigger onClick={handleOnSummaryClick}>
                <ChartCardContainer liked={liked} minimal={false} loading={false}>
                  {props.children}
                </ChartCardContainer>
              </StyledTrigger>
              <StyledContent>{props.accordionDetails}</StyledContent>
            </StyledItem>
          </div>
          <button className="md:hidden" type="button" onClick={handleOnSummaryClick}>
            <ChartCardContainer>{props.children}</ChartCardContainer>
          </button>
        </>
      )}
    </ChartCardContext.Provider>
  );
}

export const ChartCardContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  background: '$white',
  width: '100%',
  height: CHART_CARD_SIZE.mobile,
  gap: 4,
  paddingX: 0,

  '@tablet': {
    marginY: 16,

    '&:hover': {
      // Show hoverActions on tablet when main chart card is hovered
      [`& ${PlayButtonContainer}, ${HoverActions}`]: {
        opacity: 1,
      },
    },

    // Zoom in artwork when main chart card is hovered
    '@media (hover: hover)  and (pointer: fine)': {
      '&:hover': {
        [`& ${CoverImageContainer}`]: {
          img: {
            objectFit: 'cover',
            transition: 'transform 0.4s ease',
            transform: 'scale(1.2)',
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      minimal: false,
      loading: false,
      css: {
        '@tablet': {
          height: CHART_CARD_COVER_IMAGE_SIZES.tablet,

          // Show hovered state but avoid the main chart card from overflowing its container
          '&:hover:after': {
            content: '',
            background: '$neutral50',
            position: 'absolute',
            left: CHART_CARD_MARGIN * -1,
            top: CHART_CARD_MARGIN * -1,
            width: `calc(100% + ${CHART_CARD_MARGIN * 2}px)`,
            height: `calc(100% + ${CHART_CARD_MARGIN * 2}px)`,
            zIndex: '$below',
            borderRadius: '$large',
            overflow: 'hidden',
          },

          // Create a fake top border to hide the element above's border bottom when hovered
          '&:hover:before': {
            top: CHART_CARD_MARGIN * -1 - 1,
            content: '',
            position: 'absolute',
            borderTop: '1px solid $white',
            width: '100%',
            height: 1,
          },

          '@desktop': {
            height: CHART_CARD_COVER_IMAGE_SIZES.desktop,
          },

          '&:hover': {
            background: '$neutral50',
            borderRadius: '$large',

            '&:after': {
              borderTop: '1px solid $white', // Overlay previous element's bottom border
            },
          },
        },
      },
    },
    {
      minimal: false,
      loading: true,
      css: {
        '@tablet': {
          height: CHART_CARD_COVER_IMAGE_SIZES.tablet,
          '@desktop': {
            height: CHART_CARD_COVER_IMAGE_SIZES.desktop,
          },
        },
      },
    },
  ],

  variants: {
    minimal: {
      true: {
        marginY: 0,
        height: 'unset',
        zIndex: 0,

        '&:after': {
          content: '',
          position: 'absolute',
          zIndex: -1,
          inset: '-$1', // Allow :after to break outside of the grid
          borderRadius: '$large',
        },

        '&:hover:after': {
          background: '$base50',
        },
      },
      false: {
        // show line separator between chart cards
        '&:after': {
          borderTop: '1px solid $neutral100',
          position: 'absolute',
          content: '',
          width: '100%',
          height: 1,
          bottom: CHART_CARD_MARGIN * -1,
          pointerEvents: 'none', // Avoid flickering when hovering over the separator
        },
      },
    },
    liked: {
      true: {
        // If liked, always show hover actions
        [`${HoverActions}`]: {
          opacity: 1,
        },
      },
    },
    loading: {
      true: {},
    },
  },
});

const StyledItem = styled(Item, {
  "&[data-state='open']": {
    // Turn around arrow to indicate that the accordion is open
    [`& ${Arrow}`]: {
      transform: 'rotate(180deg)',
    },
    // Make card container and accordion details transition seamless by removing border radius
    [` ${ChartCardContainer}`]: {
      borderRadius: '$large $large 0 0',
      background: '$white',

      // Avoid showing hover state of opened card to avoid strange look with wider chart card than accordion
      '&:hover:before, &:hover:after': {
        display: 'none',
      },

      // Avoid showing border bottom on opened card
      '&:after': {
        display: 'none',
      },
    },
    // Show play button on mobile when accordion is open
    [`& ${PlayButtonContainer}, ${HoverActions}`]: {
      opacity: 1,
    },
    // Zoom in artwork when accordion is open
    '@media (hover: hover)  and (pointer: fine)': {
      '&:hover': {
        [`& ${CoverImageContainer}`]: {
          img: {
            objectFit: 'cover',
            transition: `transform ${ANIMATION_TIMING}`,
            transform: 'scale(1.2)',
          },
        },
      },
    },
  },
});

const StyledTrigger = styled(AccordionTrigger, {
  width: '100%',
});

const slideDown = keyframes({
  '0%': { height: 0 },
  '100%': { height: 'var(--radix-accordion-content-height)' },
});

const slideUp = keyframes({
  '0%': { height: 'var(--radix-accordion-content-height)' },
  '100%': { height: 0 },
});

const StyledContent = styled(Content, {
  overflow: 'hidden',

  "&[data-state='open']": {
    animation: `${slideDown} ${ANIMATION_TIMING}`,
  },
  "&[data-state='closed']": {
    animation: `${slideUp} ${ANIMATION_TIMING}`,
  },
});

const tabletStyles = {
  flexGrow: 1,
  marginLeft: 24,
};

const InfoAndCollectors = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
  overflow: 'hidden',
  marginLeft: 8,
  width: '100%',
  height: CHART_CARD_COVER_IMAGE_SIZES.mobile,

  '@tablet': tabletStyles,

  variants: {
    minimal: {
      true: {
        flexDirection: 'row',
        alignItems: 'center',

        '@tablet': {
          flexDirection: 'row',
        },
      },
      false: {
        '@tablet': {
          height: '100%',
        },
      },
    },
  },
});

const Artwork = styled('div', {
  position: 'relative',
});

const Row = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,

  variants: {
    minimal: {
      true: {
        width: 'unset',
      },
    },
  },
});

ChartCard.Cover = ChartCardCover;
ChartCard.Info = ChartCardInfo;
ChartCard.Rank = ChartCardRank;
ChartCard.Actions = ChartCardActions;
ChartCard.Collectors = ChartCardCollectors;
ChartCard.Button = ChartCardButton;
ChartCard.Stats = ChartCardStats;
ChartCard.AccordionDetails = ChartCardAccordionDetails;
ChartCard.Row = Row;
ChartCard.InfoAndCollectors = InfoAndCollectors;
ChartCard.Artwork = Artwork;
ChartCard.Skeleton = ChartCardSkeleton;
ChartCard.Context = ChartCardContext;
ChartCard.AvatarsSkeleton = AvatarsSkeleton;
